/**
 * Host editor marks/nodes operations adapter.
 * This is the ONLY place where editor-specific mark/node operations may be implemented.
 *
 * Responsibilities:
 * - Apply comment marks/nodes to editor
 * - Remove comment marks/nodes from editor
 * - Update marks/nodes when document changes
 *
 * Architectural Rules:
 * - MAY import editor-specific types (Editor, Element, Transforms, etc.)
 * - MUST NOT import Velt SDK types
 * - Handles editor-specific mark/node application logic
 */
import { Editor, Element, Node, Text, Transforms } from 'slate';
import { ReactEditor as ReactEditorImpl } from 'slate-react';
import { Constants } from '@/constants/common';
import { Console } from '@/utils/console';
/**
 * History editor type (if available from extension config).
 * Used to wrap operations without saving to history.
 * This is editor-specific and may vary, so we use unknown.
 */
let HistoryEditor = null;
/**
 * Sets the history editor instance (called from extension).
 */
export const setHistoryEditor = (historyEditor) => {
    HistoryEditor = historyEditor;
};
/**
 * Gets the history editor instance.
 */
export const getHistoryEditor = () => {
    return HistoryEditor;
};
/**
 * Checks if a node is a Velt comment element.
 */
const isVeltCommentText = (element) => {
    return Element.isElement(element) && element.type === Constants.ELEMENT_TYPE.VELT_COMMENT;
};
/**
 * Internal function that applies annotation mark/node to editor at specified range.
 * Wraps text range with comment element/node.
 */
const applyAnnotationMarkWithRange = (editor, annotationId, multiThreadAnnotationId, range) => {
    try {
        if (!range) {
            return false;
        }
        // Check if the range is already wrapped with a velt-comment-text element
        const isAlreadyWrapped = Array.from(Editor.nodes(editor, {
            at: range,
            match: (node) => {
                if (!Element.isElement(node)) {
                    return false;
                }
                return isVeltCommentText(node);
            },
        })).length > 0;
        if (isAlreadyWrapped) {
            return false;
        }
        // Wrap nodes with comment element
        const wrapNodes = () => {
            Transforms.wrapNodes(editor, {
                type: Constants.ELEMENT_TYPE.VELT_COMMENT,
                children: [],
                annotationId: annotationId,
                multiThreadAnnotationId: multiThreadAnnotationId,
            }, { at: range, split: true });
        };
        // Use history editor if available to wrap without saving to history
        if (HistoryEditor && typeof HistoryEditor.withoutSaving === 'function') {
            HistoryEditor.withoutSaving(editor, () => {
                wrapNodes();
            });
        }
        else {
            wrapNodes();
        }
        return true;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error applying annotation mark:', error);
        return false;
    }
};
/**
 * Applies annotation mark/node by finding text occurrence and wrapping it.
 * This is the main entry point for applying marks.
 *
 * @param editor The editor instance (typed as unknown to avoid importing editor types in feature modules)
 * @param textToSearch The text to search for
 * @param occurrence The occurrence number (1-based)
 * @param annotationId The annotation ID to mark
 * @param multiThreadAnnotationId Optional multi-thread annotation ID
 * @param targetElementId Optional target element ID to scope the search
 * @returns True if mark was successfully applied, false otherwise
 */
export const applyAnnotationMarkByText = (editor, textToSearch, occurrence, annotationId, multiThreadAnnotationId, targetElementId) => {
    const logPrefix = '[applyAnnotationMarkByText]';
    try {
        if (!editor || !annotationId) {
            return false;
        }
        const slateEditor = editor;
        // Find target element
        let targetElement = null;
        if (targetElementId) {
            targetElement = document.getElementById(targetElementId);
            // Validate target element
            if (!targetElement) {
                Console.warn(`${logPrefix} Target element not found: ${targetElementId}`);
                return false;
            }
            // Ensure the element is part of the editor
            if (!isElementInEditor(slateEditor, targetElement)) {
                Console.warn(`${logPrefix} Target element not in editor: ${targetElementId}`);
                return false;
            }
        }
        else {
            targetElement = ReactEditorImpl.toDOMNode(slateEditor, slateEditor);
        }
        if (!targetElement) {
            Console.warn(`${logPrefix} No target element found`);
            return false;
        }
        // Find the range for the specific text occurrence
        const range = findTextOccurrenceRange(slateEditor, textToSearch, occurrence, targetElement);
        if (!range) {
            Console.warn(`${logPrefix} Could not find text occurrence range for "${textToSearch}" occurrence ${occurrence}`);
            return false;
        }
        // Apply mark directly with range instead of converting to from/to
        return applyAnnotationMarkWithRange(slateEditor, annotationId, multiThreadAnnotationId, range);
    }
    catch (error) {
        Console.catch(`${logPrefix} Error applying annotation mark by text:`, error);
        return false;
    }
};
/**
 * Removes annotation mark/node from editor by annotation ID.
 * Finds all nodes with matching annotationId and unwraps them.
 */
export const removeAnnotationMark = (editor, annotationId) => {
    try {
        if (!editor || !annotationId) {
            return false;
        }
        // Create an array to store paths of elements to remove
        const elementsToUnwrap = [];
        // Iterate through all nodes in the editor
        for (const [node, path] of Node.descendants(editor)) {
            // Check if the node is a Velt comment element with matching annotationId
            if (Element.isElement(node) &&
                isVeltCommentText(node) &&
                node.annotationId === annotationId) {
                elementsToUnwrap.push(path);
            }
        }
        // Unwrap elements in reverse order to avoid path shifting issues
        try {
            elementsToUnwrap.reverse().forEach((path) => {
                const nodeAtPath = Node.get(editor, path);
                const elementAtNode = ReactEditorImpl.toDOMNode(editor, nodeAtPath);
                if (elementAtNode === null || elementAtNode === void 0 ? void 0 : elementAtNode.isConnected) {
                    const unwrapNodes = () => {
                        Transforms.unwrapNodes(editor, {
                            at: path,
                            mode: 'lowest',
                        });
                    };
                    // Use history editor if available
                    if (HistoryEditor && typeof HistoryEditor.withoutSaving === 'function') {
                        HistoryEditor.withoutSaving(editor, () => {
                            unwrapNodes();
                        });
                    }
                    else {
                        unwrapNodes();
                    }
                }
            });
        }
        catch (err) {
            Console.catch('[HostAdapter] Error unwrapping elements:', err);
        }
        return true;
    }
    catch (error) {
        Console.catch('[HostAdapter] Error removing annotation mark:', error);
        return false;
    }
};
/**
 * Updates marks/nodes based on document changes.
 * Removes old marks and applies new ones at updated positions.
 */
export const updateMarks = (editor, changes) => {
    try {
        for (const [annotationId, change] of changes) {
            // Remove old mark
            removeAnnotationMark(editor, annotationId);
            // TODO: Re-apply mark at new position if needed
            // This requires the full annotation data to rebuild the mark
            // For now, just remove - re-application will happen via renderComments
        }
    }
    catch (error) {
        Console.catch('[HostAdapter] Error updating marks:', error);
    }
};
/**
 * Helper: Checks if element is within editor DOM.
 */
const isElementInEditor = (editor, element) => {
    try {
        const editorElement = ReactEditorImpl.toDOMNode(editor, editor);
        return editorElement.contains(element);
    }
    catch (error) {
        Console.catch('[HostAdapter] Error checking element in editor:', error);
        return false;
    }
};
/**
 * Helper: Finds text occurrence range in editor.
 * Converts DOM element to Slate range for the specified occurrence.
 */
const findTextOccurrenceRange = (editor, textToSearch, occurrence, targetElement) => {
    const logPrefix = '[findTextOccurrenceRange]';
    try {
        // Convert target element to Slate node
        const targetNode = ReactEditorImpl.toSlateNode(editor, targetElement);
        if (!targetNode) {
            Console.warn(`${logPrefix} Could not convert target element to Slate node`);
            return null;
        }
        const targetPath = ReactEditorImpl.findPath(editor, targetNode);
        // Collect all text from the subtree
        const { fullText, textNodePositions } = collectSubtreeText(editor, targetNode, targetPath);
        // Find all matches in the full text
        const matches = [];
        let match;
        const regex = new RegExp(textToSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        while ((match = regex.exec(fullText)) !== null) {
            matches.push({ index: match.index });
        }
        // If we don't have enough occurrences, return null
        if (matches.length < occurrence) {
            Console.warn(`${logPrefix} Not enough occurrences: found ${matches.length}, need ${occurrence}`);
            return null;
        }
        // Get the specific match
        const targetMatch = matches[occurrence - 1];
        // Find which text nodes the match spans
        const matchStart = targetMatch.index;
        const matchEnd = matchStart + textToSearch.length;
        // Find the range that covers this match
        let anchorNodeInfo;
        let focusNodeInfo;
        for (const nodePosition of textNodePositions) {
            // Check for anchor (start of match)
            if (matchStart >= nodePosition.start && matchStart < nodePosition.end && !anchorNodeInfo) {
                anchorNodeInfo = nodePosition;
            }
            // Check for focus (end of match)
            if (matchEnd > nodePosition.start && matchEnd <= nodePosition.end && !focusNodeInfo) {
                focusNodeInfo = nodePosition;
            }
            // If we have both anchor and focus, we can create the range
            if (anchorNodeInfo && focusNodeInfo) {
                const range = {
                    anchor: {
                        path: anchorNodeInfo.path,
                        offset: matchStart - anchorNodeInfo.start,
                    },
                    focus: {
                        path: focusNodeInfo.path,
                        offset: matchEnd - focusNodeInfo.start,
                    },
                };
                return range;
            }
        }
        Console.warn(`${logPrefix} Could not find anchor/focus nodes for match`);
        return null;
    }
    catch (error) {
        Console.catch(`${logPrefix} Error finding text occurrence range:`, error);
        return null;
    }
};
/**
 * Helper: Collects subtree text and maps text node positions.
 */
const collectSubtreeText = (editor, node, path) => {
    const textNodePositions = [];
    let fullText = '';
    // Traverse nodes for text
    const traverseNodesForText = (currentNode, currentPath, textNodePositions, fullText) => {
        try {
            if (Text.isText(currentNode)) {
                const start = fullText.length;
                fullText += currentNode.text;
                const end = fullText.length;
                textNodePositions.push({
                    node: currentNode,
                    path: currentPath,
                    start,
                    end,
                });
            }
            else if (Element.isElement(currentNode)) {
                // Traverse child nodes
                for (let i = 0; i < currentNode.children.length; i++) {
                    const childPath = [...currentPath, i];
                    fullText = traverseNodesForText(currentNode.children[i], childPath, textNodePositions, fullText);
                }
            }
            return fullText;
        }
        catch (error) {
            Console.catch('[HostAdapter] Error traversing nodes for text:', error);
            return fullText;
        }
    };
    // Special case for the root node (editor itself)
    if (path.length === 0) {
        // When path is [], we're at the editor root, so traverse all children
        for (let i = 0; i < editor.children.length; i++) {
            const childPath = [i];
            fullText = traverseNodesForText(editor.children[i], childPath, textNodePositions, fullText);
        }
    }
    else {
        // Normal case for non-root nodes
        fullText = traverseNodesForText(node, path, textNodePositions, fullText);
    }
    return { fullText, textNodePositions };
};
